import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from './collections';

const DEFAULT_COMMISSION_PERCENTAGE = 10;

export const settlementsServiceOptimized = {
  getDateRange(periodType, startDate = null, endDate = null) {
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'monthly':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'yearly':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      case 'custom':
        if (startDate && endDate) {
          start = new Date(startDate);
          end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
        } else {
          start = new Date(now.getFullYear(), 0, 1);
          end = now;
        }
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    }

    return { start, end };
  },

  // Optimized: Fetch all data in parallel, then process in memory
  async getTeachersSettlements(periodType = 'monthly', startDate = null, endDate = null, commissionPercentage = DEFAULT_COMMISSION_PERCENTAGE) {
    try {
      const { start, end } = this.getDateRange(periodType, startDate, endDate);

      // Fetch all data in parallel
      const [teachersSnapshot, paymentsSnapshot, mainTransactionsSnapshot, refundsSnapshot] = await Promise.all([
        getDocs(collection(db, 'tuitionTeachers')),
        getDocs(collection(db, COLLECTIONS.TEACHER_HIRING_PAYMENTS)).catch(() => ({ docs: [] })),
        getDocs(collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS)).catch(() => ({ docs: [] })),
        getDocs(collection(db, COLLECTIONS.PAYMENT_REFUNDS)).catch(() => ({ docs: [] }))
      ]);

      // Build lookup maps for faster processing
      const paymentsByTeacher = new Map();
      const refundsByPaymentId = new Map();
      const teacherMap = new Map();

      // Index teachers
      teachersSnapshot.docs.forEach(doc => {
        teacherMap.set(doc.id, { id: doc.id, ...doc.data() });
      });

      // Index payments by teacher
      paymentsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const teacherId = data.teacherId;
        if (!teacherId) return;

        if (!paymentsByTeacher.has(teacherId)) {
          paymentsByTeacher.set(teacherId, []);
        }
        paymentsByTeacher.get(teacherId).push({ id: doc.id, ...data });
      });

      // Index main transactions
      mainTransactionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.paymentType !== 'teacher_hiring') return;

        const teacherId = data.teacherId || data.metadata?.teacherId;
        if (!teacherId) return;

        if (!paymentsByTeacher.has(teacherId)) {
          paymentsByTeacher.set(teacherId, []);
        }
        paymentsByTeacher.get(teacherId).push({ id: doc.id, ...data, source: 'main' });
      });

      // Index refunds by payment ID
      refundsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const paymentId = data.paymentId || data.razorpayPaymentId || data.transactionId;
        if (paymentId) {
          if (!refundsByPaymentId.has(paymentId)) {
            refundsByPaymentId.set(paymentId, []);
          }
          refundsByPaymentId.get(paymentId).push({ id: doc.id, ...data });
        }
      });

      // Process each teacher
      const teachers = [];
      for (const [teacherId, teacherData] of teacherMap) {
        const payments = paymentsByTeacher.get(teacherId) || [];
        
        let totalCaptured = 0;
        let totalRefunded = 0;
        let totalFailed = 0;
        const transactions = [];
        const paymentIds = new Set();

        // Process payments
        payments.forEach(payment => {
          const paymentDate = payment.paymentDate?.toDate?.() || payment.createdAt?.toDate?.() || new Date();
          if (paymentDate < start || paymentDate > end) return;

          const amount = payment.paymentAmount || payment.totalAmount || payment.amount || payment.sessionCost || 0;
          const paymentId = payment.paymentId || payment.razorpayPaymentId || payment.transactionId;
          const isCaptured = payment.captured === true || payment.captured === 'true';
          const isCompleted = payment.paymentStatus === 'completed' || payment.status === 'completed';
          const isReferralBonus = payment.paymentType === 'referral_bonus';

          if (paymentId) paymentIds.add(paymentId);

          if ((isCaptured && isCompleted) || (isReferralBonus && isCompleted)) {
            totalCaptured += amount;
            transactions.push({
              id: payment.id,
              transactionId: paymentId,
              paymentId,
              amount,
              date: paymentDate,
              studentName: payment.studentName || payment.description || 'Referral Bonus',
              status: isReferralBonus ? 'referral_bonus' : 'captured',
              captured: true,
              type: isReferralBonus ? 'referral' : 'payment'
            });
          } else if (!isCaptured && !isReferralBonus) {
            totalFailed += amount;
          }
        });

        // Calculate refunds
        paymentIds.forEach(paymentId => {
          const refunds = refundsByPaymentId.get(paymentId) || [];
          refunds.forEach(refund => {
            const refundDate = refund.createdAt?.toDate?.() || new Date();
            if (refundDate >= start && refundDate <= end) {
              totalRefunded += refund.amount || refund.refundAmount || refund.transactionAmount || 0;
            }
          });
        });

        // Calculate settlement
        const netAmount = totalCaptured - totalRefunded;
        const commissionAmount = (netAmount * commissionPercentage) / 100;
        const settlementAmount = netAmount - commissionAmount;

        if (totalCaptured > 0 || totalRefunded > 0 || totalFailed > 0) {
          teachers.push({
            id: teacherId,
            userId: teacherData.userId,
            name: teacherData.name || 'Unknown Teacher',
            email: teacherData.email || '',
            phone: teacherData.phone || '',
            bankDetails: teacherData.bankDetails || teacherData.userProfile?.bankDetails || null,
            totalCaptured,
            totalRefunded,
            totalFailed,
            netAmount,
            commissionPercentage,
            commissionAmount,
            settlementAmount,
            transactionCount: transactions.length,
            transactions: transactions.sort((a, b) => b.date - a.date),
            periodType,
            startDate: start,
            endDate: end
          });
        }
      }

      return { success: true, settlements: teachers };
    } catch (error) {
      console.error('Error in getTeachersSettlements:', error);
      return { success: false, error: error.message, settlements: [] };
    }
  },

  async getInstitutesSettlements(periodType = 'monthly', startDate = null, endDate = null, commissionPercentage = DEFAULT_COMMISSION_PERCENTAGE) {
    try {
      const { start, end } = this.getDateRange(periodType, startDate, endDate);

      // Fetch all data in parallel
      const [usersSnapshot, subscriptionPaymentsSnapshot, admissionPaymentsSnapshot, refundsSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.USERS)),
        getDocs(collection(db, COLLECTIONS.COACHING_SUBSCRIPTION_PAYMENTS)).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, COLLECTIONS.PAYMENT_TRANSACTIONS), where('paymentType', '==', 'admission_application'))).catch(() => ({ docs: [] })),
        getDocs(collection(db, COLLECTIONS.PAYMENT_REFUNDS)).catch(() => ({ docs: [] }))
      ]);

      // Build lookup maps
      const paymentsByCoaching = new Map();
      const refundsByPaymentId = new Map();
      const instituteMap = new Map();

      // Index institutes
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.isActive && (data.coachingId || data.isCoaching || data.coachingName)) {
          instituteMap.set(doc.id, { id: doc.id, ...data });
        }
      });

      // Index subscription payments
      subscriptionPaymentsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const coachingId = data.coachingId;
        if (!coachingId) return;

        if (!paymentsByCoaching.has(coachingId)) {
          paymentsByCoaching.set(coachingId, []);
        }
        paymentsByCoaching.get(coachingId).push({ id: doc.id, ...data, type: 'subscription' });
      });

      // Index admission payments
      admissionPaymentsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const coachingId = data.metadata?.institutionId;
        if (!coachingId) return;

        if (!paymentsByCoaching.has(coachingId)) {
          paymentsByCoaching.set(coachingId, []);
        }
        paymentsByCoaching.get(coachingId).push({ id: doc.id, ...data, type: 'admission' });
      });

      // Index refunds
      refundsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const paymentId = data.paymentId || data.razorpayPaymentId || data.transactionId;
        if (paymentId) {
          if (!refundsByPaymentId.has(paymentId)) {
            refundsByPaymentId.set(paymentId, []);
          }
          refundsByPaymentId.get(paymentId).push({ id: doc.id, ...data });
        }
      });

      // Process each institute
      const institutes = [];
      for (const [instituteId, instituteData] of instituteMap) {
        const coachingId = instituteData.coachingId || instituteId;
        const payments = paymentsByCoaching.get(coachingId) || [];
        
        let totalCaptured = 0;
        let totalRefunded = 0;
        let totalFailed = 0;
        const transactions = [];
        const paymentIds = new Set();

        // Process payments
        payments.forEach(payment => {
          const paymentDate = payment.paymentDate?.toDate?.() || payment.createdAt?.toDate?.() || new Date();
          if (paymentDate < start || paymentDate > end) return;

          const amount = payment.paymentAmount || payment.amount || 0;
          const paymentId = payment.paymentId || payment.razorpayPaymentId || payment.transactionId;
          const isCaptured = payment.captured === true || payment.captured === 'true';
          const isCompleted = payment.paymentStatus === 'completed' || payment.status === 'completed';

          if (paymentId) paymentIds.add(paymentId);

          if (isCaptured && isCompleted) {
            totalCaptured += amount;
            transactions.push({
              id: payment.id,
              transactionId: paymentId,
              paymentId,
              amount,
              date: paymentDate,
              type: payment.type,
              customerName: payment.contactPerson || payment.customerName || payment.metadata?.studentName,
              status: 'captured',
              captured: true
            });
          } else if (!isCaptured) {
            totalFailed += amount;
          }
        });

        // Calculate refunds
        paymentIds.forEach(paymentId => {
          const refunds = refundsByPaymentId.get(paymentId) || [];
          refunds.forEach(refund => {
            const refundDate = refund.createdAt?.toDate?.() || new Date();
            if (refundDate >= start && refundDate <= end) {
              totalRefunded += refund.amount || refund.refundAmount || refund.transactionAmount || 0;
            }
          });
        });

        // Calculate settlement
        const netAmount = totalCaptured - totalRefunded;
        const commissionAmount = (netAmount * commissionPercentage) / 100;
        const settlementAmount = netAmount - commissionAmount;

        if (totalCaptured > 0 || totalRefunded > 0 || totalFailed > 0) {
          institutes.push({
            id: instituteId,
            coachingId,
            name: instituteData.coachingName || instituteData.displayName || 'Unknown Institute',
            email: instituteData.email || '',
            phone: instituteData.phone || instituteData.contactPersonPhone || '',
            bankDetails: instituteData.bankDetails || null,
            totalCaptured,
            totalRefunded,
            totalFailed,
            netAmount,
            commissionPercentage,
            commissionAmount,
            settlementAmount,
            transactionCount: transactions.length,
            transactions: transactions.sort((a, b) => b.date - a.date),
            periodType,
            startDate: start,
            endDate: end
          });
        }
      }

      return { success: true, settlements: institutes };
    } catch (error) {
      console.error('Error in getInstitutesSettlements:', error);
      return { success: false, error: error.message, settlements: [] };
    }
  },

  async updateSettlementTransferStatus(entityType, entityId, settlementId, transferStatus, transferDetails = {}) {
    try {
      return { success: true, message: 'Transfer status updated', transferStatus, transferDetails };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};
